import axios, { AxiosInstance, AxiosResponse } from 'axios';
import accessLocalStorage from 'services/localStorage';

/**
 * Normalizes the Selltreez base URL to ensure it has the https:// prefix
 * @param baseUrl - The base URL from organization settings
 * @returns Normalized URL with https:// prefix
 */
const normalizeSelltreezUrl = (baseUrl: string | undefined): string => {
  if (!baseUrl) {
    throw new Error('Selltreez URL not configured for this organization');
  }

  return baseUrl.startsWith('http://') || baseUrl.startsWith('https://')
    ? baseUrl
    : `https://${baseUrl}`;
};

/**
 * Creates an axios instance configured for Selltreez HintsService API calls
 * This instance includes:
 * - Proper base URL normalization
 * - Authorization header with access token
 * - X-Organization-Id header
 * - Credentials disabled for CORS
 *
 * @param selltreezBaseUrl - The base URL from organization settings (e.g., sell_treez_url)
 * @returns Configured AxiosInstance
 */
export const createSelltreezApiInstance = (
  selltreezBaseUrl: string | undefined
): AxiosInstance => {
  const baseURL = normalizeSelltreezUrl(selltreezBaseUrl);
  const user = accessLocalStorage.getUser();
  const orgId = accessLocalStorage.getOrgId();

  const instance = axios.create({
    baseURL,
    withCredentials: false,
    headers: {
      Authorization: `Bearer ${user.accessToken}`,
      'X-Organization-Id': orgId,
    },
  });

  return instance;
};

/**
 * Wrapper class for Selltreez API calls
 * Provides a consistent interface similar to the main Api class
 */
export class SelltreezApi {
  private axiosInst: AxiosInstance;

  constructor(selltreezBaseUrl: string | undefined) {
    this.axiosInst = createSelltreezApiInstance(selltreezBaseUrl);
  }

  /**
   * GET request to Selltreez API
   */
  get = <T>(
    url: string,
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<T>> => {
    return this.axiosInst.get<T>(url, { params });
  };

  /**
   * POST request to Selltreez API
   */
  post = <T>(
    url: string,
    data?: Record<string, unknown>
  ): Promise<AxiosResponse<T>> => {
    return this.axiosInst.post<T>(url, data);
  };

  /**
   * PUT request to Selltreez API
   */
  put = <T>(
    url: string,
    data?: Record<string, unknown>
  ): Promise<AxiosResponse<T>> => {
    return this.axiosInst.put<T>(url, data);
  };

  /**
   * DELETE request to Selltreez API
   */
  delete = <T>(url: string): Promise<AxiosResponse<T>> => {
    return this.axiosInst.delete<T>(url);
  };
}

export default SelltreezApi;
